import React, { useState, useEffect } from 'react';
import OverallTab from '../components/Tabs/OverallTab';
import PortsTab from '../components/Tabs/PortsTab';
import EntityTrafficTab from '../components/Tabs/EntityTrafficTab';
import MilestoneTab from '../components/Tabs/MilestoneTab';
import CommodityTab from '../components/Tabs/CommodityTab';
import ImportExportTab from '../components/Tabs/ImportExportTab';
import ForecastingTab from '../components/Tabs/ForecastingTab';
import DrillDownModal from '../components/DrillDownModal'; 
import '../styles/Dashboard.css';

// Import Chart.js globally
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';

// Register ChartJS components globally
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const Dashboard = () => {
  const [currentTab, setCurrentTab] = useState('overall');
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [lastUpdated, setLastUpdated] = useState('Today 14:30 UTC');
  const [selectedPort, setSelectedPort] = useState('port1');
  const [drillDownData, setDrillDownData] = useState(null);
  const [isDrillDownOpen, setIsDrillDownOpen] = useState(false);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const dateOptions = { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      setCurrentDateTime(now.toLocaleDateString('en-US', dateOptions));
      
      const timeString = now.getHours().toString().padStart(2, '0') + ':' + 
                         now.getMinutes().toString().padStart(2, '0');
      setLastUpdated(`Today ${timeString} UTC`);
    };

    updateTime();
    const timeInterval = setInterval(updateTime, 60000);
    return () => clearInterval(timeInterval);
  }, []);

  // Function to handle drill-down
  const openDrillDown = (drilldownType, data, title) => {
    console.log('Opening drill-down:', drilldownType, data, title);
  
     // Prepare data based on type
    let preparedData = data;
    let drilldownTitle = title || 'Detailed View';
    
    // If data is just an ID string, create a basic object
    if (typeof data === 'string') {
      preparedData = {
        id: data,
        name: data.replace('-', ' ').toUpperCase(),
        title: drilldownTitle
      };
    }
    
    setDrillDownData({
      type: drilldownType,
      data: preparedData,
      title: drilldownTitle
    });
    setIsDrillDownOpen(true);
  };

  // Function to handle modal close
  const closeDrillDown = () => {
    setIsDrillDownOpen(false);
    setDrillDownData(null);
  };

  // Function to show details (for backward compatibility)
  const showDetails = (id, data) => {
    const drilldownType = id.includes('-') ? id.split('-')[0] : id;
    openDrillDown(drilldownType, data || id, `${drilldownType} Details`);
  };

  // Tabs configuration with consistent structure
  const tabs = [
    { id: 'overall', label: 'Overall Summary', icon: 'fas fa-chart-pie' },
    { id: 'ports', label: 'Multi-Port View', icon: 'fas fa-globe-americas' },
    { id: 'commodity', label: 'Commodity Traffic', icon: 'fas fa-boxes' },
    { id: 'import-export', label: 'Import/Export', icon: 'fas fa-exchange-alt' },
    { id: 'entity-traffic', label: 'Entity Traffic', icon: 'fas fa-ship' },
    { id: 'milestone', label: 'Million Marks', icon: 'fas fa-trophy' },
    { id: 'forecasting', label: 'Forecasting', icon: 'fas fa-chart-line' }
  ];

 const handleTabChange = (tabId) => {
    setCurrentTab(tabId);
  };

  // Update render functions to pass drill-down handlers
  const renderOverallTab = () => (
    <OverallTab 
      selectedPort={selectedPort}
      showDetails={showDetails}
      openDrillDown={openDrillDown}
    />
  );

  const renderPortsTab = () => (
    <PortsTab 
      selectedPort={selectedPort}
      handlePortChange={setSelectedPort}
      openDrillDown={openDrillDown}
    />
  );

  const renderCommodityTab = () => (
    <CommodityTab 
      showDetails={showDetails}
      openDrillDown={openDrillDown}
    />
  );

  const renderImportExportTab = () => (
    <ImportExportTab 
      showDetails={showDetails}
      openDrillDown={openDrillDown}
    />
  );

  const renderEntityTrafficTab = () => (
    <EntityTrafficTab 
      showDetails={showDetails}
      openDrillDown={openDrillDown}
    />
  );

  const renderMilestoneTab = () => (
    <MilestoneTab 
      showDetails={showDetails}
      openDrillDown={openDrillDown}
    />
  );

  const renderForecastingTab = () => (
    <ForecastingTab 
      showDetails={showDetails}
      openDrillDown={openDrillDown}
    />
  );

  const renderTabContent = () => {
    switch(currentTab) {
      case 'overall':
        return renderOverallTab();
      case 'ports':
        return renderPortsTab();
      case 'commodity':
        return renderCommodityTab();
      case 'import-export':
        return renderImportExportTab();
      case 'entity-traffic':
        return renderEntityTrafficTab();
      case 'milestone':
        return renderMilestoneTab();
      case 'forecasting':
        return renderForecastingTab();
      default:
        return renderOverallTab();
    }
  };

  return (
    <div className="dashboard-page">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-title">
          <h1>Port Operations Dashboard</h1>
          <p>Real-time monitoring of port activities and cargo analytics</p>
        </div>
        <div className="header-actions">
          <div className="date-time btn-primary">
            <i className="fas fa-clock"></i>
            {currentDateTime}
          </div>
          <div className="action-buttons">
            <button className="btn btn-primary">
              <i className="fas fa-file-excel"></i> Export
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`dashboard-tab ${currentTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <i className={tab.icon}></i>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="dashboard-content">
        {renderTabContent()}
      </div>

      {/* Drill Down Modal */}
      <DrillDownModal 
        isOpen={isDrillDownOpen}
        onClose={closeDrillDown}
        title={drillDownData?.title || 'Detailed View'}
        data={drillDownData?.data}
        type={drillDownData?.type}
      />
    </div>
  );
};

export default Dashboard;